import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { equals, pick, sort } from 'remeda';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, shareReplay, skip, switchMap, tap } from 'rxjs/operators';
import { SnackBarService } from '../shared/snackbar.service';
import { RevenueAllocationItem, RevenueAllocationResponse } from '../types';
import { sortByName } from '../utilities/commonSorters';

export interface GraphDataInfo {
  regionId: number;
  daysToLookAhead: number;
  revenueAllocationItems: RevenueAllocationItem[];
  lastUpdatedBy: 'local' | 'db' | 'calculation' | 'regionChange';
  lastUpdatedAt: number;
  isPossible: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RevenueAllocationService {
  private graphDataInfo$$ = new BehaviorSubject<GraphDataInfo>({
    regionId: 1,
    daysToLookAhead: 7,
    revenueAllocationItems: [],
    lastUpdatedBy: 'local',
    lastUpdatedAt: new Date().getTime(),
    isPossible: true,
  });
  public graphDataInfo$ = this.graphDataInfo$$.asObservable();
  private graphData$$ = new BehaviorSubject<RevenueAllocationResponse>(null);
  private graphDataShared$ = this.graphData$$.asObservable().pipe(shareReplay(1));
  public get graphData$(): Observable<RevenueAllocationResponse> {
    return this.graphDataShared$;
  }
  constructor(private http: HttpClient, private logger: NGXLogger, private snackBar: SnackBarService) {
    this.loadGraphData();
    this.graphDataInfo$$
      .pipe(
        skip(1),
        distinctUntilChanged((a, b) => equals(a, b)),
        filter((gdr) => gdr.lastUpdatedBy === 'local'), // Only do a calculation if the update was local
        debounceTime(250),
        switchMap((gdr) => {
          return this.http
            .post<RevenueAllocationResponse>(`${environment.api}/v1/internal/calculate_revenue_allocation`, {
              ...pick(gdr, ['regionId', 'daysToLookAhead']),
              earningsTargetBySubcontractorID: revenueAllocationItemsToMap(gdr.revenueAllocationItems),
            })
            .pipe(
              tap((response) => {
                this.updateGraphDataRequestIfTimesMatch(
                  gdr.lastUpdatedAt,
                  response.matchesDb ? 'db' : 'calculation',
                  response,
                );
              }),
            );
        }),
      )
      .subscribe(
        (response: RevenueAllocationResponse) => {
          this.graphData$$.next(response);
        },
        (error) => {
          this.logger.error(error);
          this.snackBar.showError(`Err ${(error as HttpErrorResponse).message}`);
        },
      );
  }

  public setRegion(regionId: number) {
    this.graphDataInfo$$.next({
      ...this.graphDataInfo$$.value,
      regionId,
      lastUpdatedBy: 'regionChange',
      lastUpdatedAt: new Date().getTime(),
    });
    this.loadGraphData();
  }

  public setDaysToLookAhead(daysToLookAhead: number) {
    this.graphDataInfo$$.next({
      ...this.graphDataInfo$$.value,
      daysToLookAhead,
      lastUpdatedBy: 'local',
      lastUpdatedAt: new Date().getTime(),
    });
  }

  public setTarget(carrierId: string, targetCents: number) {
    const currentValue = this.graphDataInfo$$.value;
    const index = currentValue.revenueAllocationItems.findIndex((item) => item.id === carrierId);
    if (index < 0) {
      this.snackBar.showError('Failed to find this carrier, try refreshing? or calling ben');
      return;
    }
    const copyOfItems = [...currentValue.revenueAllocationItems];
    const copyOfCarrier = {
      ...copyOfItems[index],
    };
    if (targetCents) {
      copyOfCarrier.isPriority = true;
      copyOfCarrier.current = targetCents;
    } else {
      copyOfCarrier.isPriority = false;
      copyOfCarrier.current = null;
    }
    copyOfItems[index] = copyOfCarrier;
    this.graphDataInfo$$.next({
      ...currentValue,
      revenueAllocationItems: copyOfItems,
      lastUpdatedBy: 'local',
      lastUpdatedAt: new Date().getTime(),
    });
  }

  public async saveData() {
    const gdr = this.graphDataInfo$$.value;
    if (gdr.lastUpdatedBy !== 'calculation') {
      this.snackBar.showError('Please wait until the calculations have run before saving');
      return;
    }
    try {
      const response = await this.http
        .post<RevenueAllocationResponse>(`${environment.api}/v1/internal/update_revenue_allocation`, {
          ...pick(gdr, ['regionId', 'daysToLookAhead']),
          earningsTargetBySubcontractorID: revenueAllocationItemsToMap(gdr.revenueAllocationItems),
        })
        .toPromise();
      this.graphData$$.next(response);
      this.updateGraphDataRequestIfTimesMatch(gdr.lastUpdatedAt, 'db', response);
      this.snackBar.showMessage('Targets Updated');
    } catch (error) {
      this.snackBar.showError(`Err ${(error as HttpErrorResponse).message}`);
      this.logger.error(error);
    }
  }

  private async loadGraphData() {
    const gdr = this.graphDataInfo$$.value;
    try {
      const response = await this.http
        .post<RevenueAllocationResponse>(
          `${environment.api}/v1/internal/calculate_revenue_allocation`,
          pick(gdr, ['regionId', 'daysToLookAhead']),
        )
        .toPromise();
      this.graphData$$.next(response);
      this.updateGraphDataRequestIfTimesMatch(gdr.lastUpdatedAt, 'db', response);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private updateGraphDataRequestIfTimesMatch(
    originalTime: number,
    lastUpdatedBy: 'db' | 'calculation',
    response: RevenueAllocationResponse,
  ) {
    const currentUpdate = this.graphDataInfo$$.value.lastUpdatedAt;
    // Changes have been made since we hit the server
    if (currentUpdate > originalTime) {
      return;
    }

    this.graphDataInfo$$.next({
      ...this.graphDataInfo$$.value,
      revenueAllocationItems: sort(response.graphData || [], sortByName),
      isPossible: response.isPossible,
      lastUpdatedBy,
      lastUpdatedAt: new Date().getTime(),
    });
  }
}

function revenueAllocationItemsToMap(items: RevenueAllocationItem[]): Record<string, number> {
  return (items || []).reduce((acc, item) => {
    if (item.isPriority) {
      acc[item.id] = item.current || 0;
    }
    return acc;
  }, {});
}
